"use client";

import { type ReactNode, useEffect, useRef } from "react";
import type { ResponsiveValue } from "@chakra-ui/react";
import { Button } from "@/components";
import { Modal, ModalBody, ModalFooter } from "@/components/ui";
import { MODAL_MAX_HEIGHT } from "@/lib/layout/viewport";
import { useThemeTokens } from "@/lib/theme/tokens";

interface FormModalProps {
  isOpen: boolean;
  heading: string;
  formId?: string;
  onClose: () => void;
  children: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  submitLabel?: string;
  cancelLabel?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  getSubmitDisabled?: () => boolean;
  size?: string | { base: string; md: string };
  /** Max height of scrollable body; defaults to nearly full viewport on mobile */
  bodyMaxH?: ResponsiveValue<string>;
  /** When true, body scrolls on small screens and footer stays visible (default true for long forms) */
  scrollableBody?: boolean;
  /** Root modal hook; `-cancel-button` / `-confirm-button` on footer actions */
  "data-testid"?: string;
}

export const FormModal = ({
  isOpen,
  onClose,
  heading,
  children,
  onSubmit,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  isLoading = false,
  isSubmitting = false,
  submitDisabled = false,
  formId,
  size = { base: "lg", md: "xl" },
  bodyMaxH,
  loadingText,
  getSubmitDisabled,
  scrollableBody = true,
  "data-testid": modalTestId,
}: FormModalProps) => {
  const theme = useThemeTokens();
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (formId && isOpen) {
      const form = document.getElementById(formId) as HTMLFormElement;
      if (form) {
        formRef.current = form;
      }
    }
  }, [formId, isOpen]);

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    } else if (formId) {
      const form =
        formRef.current || (document.getElementById(formId) as HTMLFormElement);
      if (form) {
        form.requestSubmit();
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={heading}
      size={size}
      contentProps={modalTestId ? { "data-testid": modalTestId } : undefined}
    >
      <ModalBody
        py={8}
        px={{ base: 4, md: 6 }}
        maxH={
          scrollableBody
            ? bodyMaxH ?? MODAL_MAX_HEIGHT.bodyDefault
            : undefined
        }
        overflowY={scrollableBody ? "auto" : undefined}
      >
        {children}
      </ModalBody>
      <ModalFooter
        pt={4}
        px={{ base: 4, md: 6 }}
        gap={3}
        borderTopWidth="1px"
        borderTopColor={theme.borderGlass.default}
        flexDirection={{ base: "column-reverse", sm: "row" }}
      >
        <Button
          variant="outline"
          onClick={onClose}
          isDisabled={isLoading || isSubmitting}
          flex={{ base: "1", sm: "none" }}
          {...(modalTestId
            ? { "data-testid": `${modalTestId}-cancel-button` }
            : {})}
        >
          {cancelLabel}
        </Button>
        {onSubmit || formId ? (
          <Button
            variant="primary"
            onClick={handleSubmit}
            type={formId ? "button" : "submit"}
            isLoading={isLoading || isSubmitting}
            loadingText={loadingText || submitLabel}
            isDisabled={
              submitDisabled ||
              (getSubmitDisabled ? getSubmitDisabled() : false) ||
              isSubmitting
            }
            flex={{ base: "1", sm: "none" }}
            {...(modalTestId
              ? { "data-testid": `${modalTestId}-confirm-button` }
              : {})}
          >
            {submitLabel}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};
